import { useAuth } from '@clerk/react-router';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { UserContext } from '~/app';
import { Button } from '~/components/buttons/Button/Button';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { paths } from '~/routing/routes';
import { captureSession } from '~/utils/sessionBoundary';
import { acceptColleagueInvitation } from './api';
import { INVITE_ALREADY_MEMBER_MESSAGE, INVITE_CONFLICT_MESSAGES } from './inviteConflictMessages';

/**
 * [MCV-1579] The accept endpoint's failures were rendered verbatim, so an
 * invitee saw developer copy ("Authenticated user email does not match the
 * invitation email") next to a single Go to Homepage button — a dead end, with
 * no hint that signing in as the invited address is the whole fix.
 *
 * The API sends `detail` as a string for the ClientException cases and as
 * `{ error, currentOrgName }` for the two conflicts.
 */
const describeError = (detail: string): { message: string; canRetryAsSomeoneElse: boolean } => {
  if (detail.includes('does not match the invitation email')) {
    return {
      message:
        'This invitation was sent to a different email address than the one you are signed in with. Sign out and sign back in with the invited address to accept it.',
      canRetryAsSomeoneElse: true,
    };
  }
  if (detail === 'ALREADY_EMPLOYER_AT_OTHER_ORG') {
    // [MCV-1796] Signing out and back in as the same account cannot change this.
    return { message: INVITE_CONFLICT_MESSAGES.other_org, canRetryAsSomeoneElse: false };
  }
  if (detail === 'ALREADY_REGISTERED_WITH_DIFFERENT_ROLE') {
    return { message: INVITE_CONFLICT_MESSAGES.provider_account, canRetryAsSomeoneElse: false };
  }
  if (detail.includes('employer user with this email already exists')) {
    return { message: INVITE_ALREADY_MEMBER_MESSAGE, canRetryAsSomeoneElse: false };
  }
  if (detail.includes('no longer pending')) {
    return {
      message: 'This invitation has already been used, revoked, or has expired.',
      canRetryAsSomeoneElse: false,
    };
  }
  if (detail.includes('Invalid invitation code')) {
    return {
      message: 'This invitation link is not valid. Ask your administrator to send a new one.',
      canRetryAsSomeoneElse: false,
    };
  }
  if (detail.includes('does not have an email address')) {
    return {
      message:
        'Your account has no email address on file, which is required to join an employer organization.',
      canRetryAsSomeoneElse: false,
    };
  }
  return { message: detail, canRetryAsSomeoneElse: false };
};

export const EmployerUserInvitationAccept = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const userRef = UserContext.useActorRef();
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let active = true;
    // The acceptance belongs to the account that opened this link. If the session
    // changes before the API answers, neither the machine nor the page may act on it.
    const session = captureSession();
    let navigateTimer: ReturnType<typeof setTimeout> | undefined;
    setError(null);
    setAccepted(false);

    if (!code) {
      setError('Invitation code is missing.');
      return;
    }

    acceptColleagueInvitation(code)
      .then((response) => {
        if (!active || !session.isCurrent()) return;
        userRef.send({
          type: 'ON_EMPLOYER_INVITE_ACCEPTED',
          sessionEpoch: session.epoch,
          output: {
            employerUserId: response.employerUserId,
            employerId: response.employerId,
            roleCode: 'EMPLOYER',
          },
        });
        setAccepted(true);
        navigateTimer = setTimeout(() => {
          if (session.isCurrent()) navigate(paths.hlx.dashboard, { replace: true });
        }, 250);
      })
      .catch((err: unknown) => {
        if (!active || !session.isCurrent()) return;
        const errorResponse = err as { response?: { data?: { detail?: unknown } } };
        const detail = errorResponse.response?.data?.detail;
        if (typeof detail === 'string') {
          setError(detail);
        } else if (detail && typeof detail === 'object' && 'error' in detail) {
          setError(String(detail.error));
        } else {
          setError('Unable to accept this invitation.');
        }
      });

    return () => {
      active = false;
      clearTimeout(navigateTimer);
    };
  }, [code, navigate, userRef]);

  if (error) {
    const { message, canRetryAsSomeoneElse } = describeError(error);

    return (
      <div className="flex min-h-screen items-center justify-center bg-base p-10">
        <div className="w-full max-w-md rounded-lg bg-white p-10 text-center shadow-xl">
          {/* `bg-error-50` is NOT a light tint — the `-50` step is the saturated
              mid-tone, so this painted a red glyph on a red disc and QA's
              recording shows it as a solid orange blob with no icon at all.
              `danger-subtle` is the tint built to pair with `text-danger`,
              mirroring what the success branch below already does. [MCV-1579] */}
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-danger-subtle">
            <ExclamationTriangleIcon className="h-7 w-7 text-danger" />
          </div>
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">
            Invitation Could Not Be Accepted
          </h1>
          <p className="mb-7 text-sm leading-relaxed text-content-secondary">{message}</p>
          <div className="flex flex-col items-center gap-3">
            {canRetryAsSomeoneElse && code && (
              <Button
                type="button"
                colorVariant="primary"
                cyTag="invite-error-sign-out"
                onClick={() => {
                  const back = `${paths.employerUserInvitation.verify}?code=${encodeURIComponent(code)}`;
                  signOut({ redirectUrl: back }).catch(() => window.location.assign(back));
                }}
              >
                Sign out and try again
              </Button>
            )}
            <Button
              type="button"
              colorVariant={canRetryAsSomeoneElse ? 'secondary' : 'primary'}
              onClick={() => navigate(paths.landing)}
            >
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base p-10">
        <div className="w-full max-w-md rounded-lg bg-white p-10 text-center shadow-xl">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-good/10">
            <CheckCircleIcon className="h-7 w-7 text-good" />
          </div>
          <h1 className="mb-3 text-2xl font-semibold text-content-primary">Invitation Accepted</h1>
          <p className="text-sm leading-relaxed text-content-secondary">
            Opening your employer workspace.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-base">
      <LoadingSpinner size={20} />
    </div>
  );
};
